import { useAppDispatch, useAppSelector } from '@/hooks/store';
import { setCurrentScreenId } from '@/state/modal';
import { Children, useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { ThemeUIStyleObject } from 'theme-ui';
import Next from './components/next';
import Screen from './components/screen';
import { modalContentPopCSS, modalOverlayCSS, modalPopCSS } from './styles';

type ModalProps = {
  children?: (JSX.Element | null)[] | JSX.Element | null;
  contentStyle?: ThemeUIStyleObject;
  initial?: string;
  parent?: Element | string;
  visible: boolean;
  zIndex?: number;
};

const Modal = ({
  children,
  contentStyle,
  initial,
  parent,
  visible,
  zIndex,
}: ModalProps) => {
  const dispatch = useAppDispatch();
  const parentEl: Element = useMemo(() => {
    const el =
      typeof parent === 'string' ? document.querySelector(parent) : parent;
    return el || document.body;
  }, [parent]);
  const [screen, setScreen] = useState<JSX.Element | null>();
  const [includesScreens, setIncludesScreens] = useState<boolean>(false);
  const currentScreenID = useAppSelector((state) => {
    return state.modal.currentScreenID || initial || '1';
  });
  const contentStyleSx = useMemo(
    () => ({ ...modalContentPopCSS, ...contentStyle }),
    [contentStyle],
  );
  const popStyleSx = useMemo(
    () => (zIndex ? { ...modalPopCSS, zIndex } : modalPopCSS),
    [zIndex],
  );
  const overlayStyleSx = useMemo(
    () =>
      zIndex ? { ...modalOverlayCSS, zIndex: zIndex - 1 } : modalOverlayCSS,
    [zIndex],
  );

  const childArray = useMemo(
    () => Children.toArray(children) as (JSX.Element | null)[],
    [children],
  );

  useEffect(() => {
    // IF no screens exist as a child, just show children
    setIncludesScreens(
      childArray?.find((child: JSX.Element | null) => child?.props?.screen) !==
        undefined,
    );
  }, [childArray]);

  useEffect(() => {
    if (!childArray.length || !includesScreens) return;

    const [currentScreen] = childArray.filter((child: JSX.Element | null) => {
      return child?.props?.screen === currentScreenID.toString();
    });

    dispatch(setCurrentScreenId(currentScreenID));
    setScreen(currentScreen);
  }, [children, currentScreenID]);

  return visible
    ? ReactDOM.createPortal(
        <>
          <div sx={popStyleSx}>
            <div sx={contentStyleSx}>{includesScreens ? screen : children}</div>
          </div>
          <div sx={overlayStyleSx} />
        </>,
        parentEl,
      )
    : null;
};

Modal.Next = Next;
Modal.Screen = Screen;

export default Modal;
